import React, { useCallback } from 'react';
import { useTranslations } from '@unisporkal/localization';
import { RawHtml } from '@unisporkal/utilities';
import {
  AlignItemsEnum,
  Flex,
  FlexDirectionEnum,
  Typography,
  TypographyComponentEnum,
  TypographyVariant,
} from '@unisporkal/alliance-ui';

import AgencyUnlimitedIcon from '../../../../icons/AgencyUnlimitedIcon';
import styles from './AgencyUnlimitedLicenseMessage.module.scss';
import acquisitionTypes from '../../../../support/acquisitionTypes';
import type { AcquisitionType } from '../../../../support/acquisitionTypes';

interface AgencyUnlimitedLicenseMessageProps {
  setAcquisitionType: (type: AcquisitionType) => void;
}

const AgencyUnlimitedLicenseMessage: React.FC<
  AgencyUnlimitedLicenseMessageProps
> = ({ setAcquisitionType }) => {
  const t = useTranslations();

  const handleTabSwitch = useCallback(() => {
    setAcquisitionType(acquisitionTypes.DOWNLOAD);
  }, [setAcquisitionType]);

  const handleInteraction = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
    ) => {
      const target = e.target as HTMLElement;
      const isClick = e.type === 'click' && target.tagName === 'A';
      const isEnterKey =
        e.type === 'keydown' && (e as React.KeyboardEvent).key === 'Enter';

      if (isClick || isEnterKey) {
        e.preventDefault();
        handleTabSwitch();
      }
    },
    [handleTabSwitch]
  );

  const messageText = React.useMemo(
    () => (
      <RawHtml>
        {t('return_to_easy_access_tab', {
          links: ['#'],
        })}
      </RawHtml>
    ),
    [t]
  );

  return (
    <Flex
      alignItems={AlignItemsEnum.FLEXSTART}
      flexDirection={FlexDirectionEnum.ROW}
      className={styles.agencyUnlimitedContainer}
      data-testid="agency-unlimited-license-message"
    >
      <div className={styles.iconContainer}>
        <AgencyUnlimitedIcon />
      </div>
      <div className={styles.content}>
        <Typography
          variant={TypographyVariant.title.small}
          component={TypographyComponentEnum.DIV}
          className={styles.title}
          data-testid="agency-unlimited-title"
        >
          {t('included_unlimited_content_plan')}
        </Typography>
        <div
          onClick={handleInteraction}
          onKeyDown={handleInteraction}
          data-testid="easy-access-tab-message"
          role="link"
          tabIndex={0}
        >
          <Typography
            variant={TypographyVariant.body.small}
            component={TypographyComponentEnum.DIV}
            className={styles.message}
          >
            {messageText}
          </Typography>
        </div>
      </div>
    </Flex>
  );
};

export default AgencyUnlimitedLicenseMessage;
